(() => {
  const SELECTOR = '[data-product-reviews-panel]';
  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');
  const TRUSTOO_REVIEWS_ENDPOINT = 'https://api.trustoo.io/api/v1/reviews/get_product_reviews';
  const HELPFUL_STORAGE_PREFIX = 'aykah:review-helpful:v1';
  const REVIEW_ORDER_STORAGE_PREFIX = 'aykah:review-order:v1';
  const VIDEO_LOAD_TIMEOUT = 7000;

  class ProductReviewsPanel {
    constructor(section) {
      this.section = section;
      this.viewport = section.querySelector('[data-pr-viewport]');
      this.track = section.querySelector('[data-pr-track]');
      this.primarySet = section.querySelector('[data-pr-set]');
      this.cards = Array.from(this.primarySet?.querySelectorAll('[data-pr-card]') || []);
      this.previousButton = section.querySelector('[data-pr-track-prev]');
      this.nextButton = section.querySelector('[data-pr-track-next]');
      this.modal = section.querySelector('[data-pr-modal]');
      this.dialog = section.querySelector('[data-pr-dialog]');
      this.reviews = [];
      this.activeReviewIndex = 0;
      this.activePhotoIndex = 0;
      this.returnFocus = null;
      this.closeTimer = null;
      this.touchStart = null;
      this.carouselTouchStart = null;
      this.marqueeAnimation = null;
      this.marqueeSecondsPerCard = Math.max(20, Number(section.dataset.prMarqueeSpeed || 55)) / Math.max(this.cards.length, 1);
      this.marqueeDuration = this.marqueeSecondsPerCard * Math.max(this.cards.length, 1) * 1000;
      this.marqueeDistance = 0;
      this.pauseReasons = new Set();
      this.resizeTimer = null;
      this.helpfulStorageKey = this.getHelpfulStorageKey();
      this.helpfulReviews = this.readHelpfulReviews();
      this.activeMediaNaturalSize = null;
      this.videoLoadTimer = null;
      this.isPhotoFirstV2 = section.dataset.prVariation === 'photo_first_v2';
      this.v2State = {
        mediaType: 'all',
        rating: 0,
        search: '',
        sort: 'relevance',
        visiblePages: 1
      };
      this.v2AllMedia = [];
      this.v2Elements = null;
      this.v2DisplayedReviewIndices = [];
      this.v2ReviewChangeTimer = null;
      this.v2LayoutAnimation = null;

      if (!this.viewport || !this.track || !this.primarySet || !this.cards.length || !this.modal || !this.dialog) return;

      this.randomizeReviewOrder();
      this.updateReviewCountState();
      this.prepareMarquee();
      this.readReviewData();
      this.cacheModalElements();
      if (this.isPhotoFirstV2) {
        this.cacheV2Elements();
        this.refreshV2();
      }
      this.bindEvents();
      this.startMarquee();
      this.updateTrackControls();
      this.loadTrustooData();
    }

    randomizeReviewOrder() {
      const occurrences = new Map();
      const entries = this.cards.map((card, originalIndex) => {
        const reviewId = card.dataset.prReviewId || `review-${originalIndex}`;
        const occurrence = occurrences.get(reviewId) || 0;
        occurrences.set(reviewId, occurrence + 1);
        return {
          card,
          key: `${reviewId}::${occurrence}`
        };
      });
      if (entries.length < 2) return;

      const originalOrder = entries.map((entry) => entry.key);
      const entriesByKey = new Map(entries.map((entry) => [entry.key, entry]));
      const productId = this.section.dataset.prProductId || 'product';
      const storageKey = `${REVIEW_ORDER_STORAGE_PREFIX}:${productId}:${entries.length}`;
      let order = null;

      try {
        const storedOrder = JSON.parse(window.sessionStorage.getItem(storageKey) || 'null');
        if (
          Array.isArray(storedOrder)
          && storedOrder.length === originalOrder.length
          && storedOrder.every((key) => entriesByKey.has(key))
        ) {
          order = storedOrder;
        }
      } catch (error) {
        // A fresh order is still available when session storage is restricted.
      }

      if (!order) {
        order = originalOrder.slice();
        for (let index = order.length - 1; index > 0; index -= 1) {
          const randomIndex = Math.floor(Math.random() * (index + 1));
          [order[index], order[randomIndex]] = [order[randomIndex], order[index]];
        }

        if (order.every((key, index) => key === originalOrder[index])) {
          order.push(order.shift());
        }

        try {
          window.sessionStorage.setItem(storageKey, JSON.stringify(order));
        } catch (error) {
          // The randomized DOM order does not depend on browser storage.
        }
      }

      order.forEach((key) => this.primarySet.appendChild(entriesByKey.get(key).card));
      this.cards = Array.from(this.primarySet.querySelectorAll('[data-pr-card]'));
      this.cards.forEach((card, reviewIndex) => {
        card.dataset.prReviewIndex = String(reviewIndex);
      });
    }

    hasCompactReviewSet() {
      return this.cards.length <= 2;
    }

    updateReviewCountState() {
      const isCompact = this.hasCompactReviewSet();
      this.section.classList.toggle('is-compact-review-set', isCompact);

      if (isCompact) {
        this.marqueeAnimation?.cancel();
        this.marqueeAnimation = null;
        this.marqueeDistance = 0;
        this.track.querySelector('[data-pr-clone]')?.remove();
        this.track.style.transform = '';
      }

      return isCompact;
    }

    prepareMarquee() {
      if (this.updateReviewCountState()) return;
      if (this.track.querySelector('[data-pr-clone]')) return;
      const clone = this.primarySet.cloneNode(true);
      clone.dataset.prClone = '';
      clone.removeAttribute('data-pr-set');
      clone.setAttribute('aria-hidden', 'true');
      clone.querySelectorAll('button, a, [tabindex]').forEach((element) => {
        element.setAttribute('tabindex', '-1');
      });
      this.track.appendChild(clone);
    }

    syncV2Marquee() {
      if (!this.isPhotoFirstV2) return;

      const hadAnimation = Boolean(this.marqueeAnimation);
      const currentTime = Number(this.marqueeAnimation?.currentTime || 0);
      const progress = this.marqueeDuration
        ? (currentTime % this.marqueeDuration) / this.marqueeDuration
        : 0;

      this.marqueeAnimation?.cancel();
      this.marqueeAnimation = null;
      this.track.querySelector('[data-pr-clone]')?.remove();
      this.prepareMarquee();

      if (hadAnimation) this.startMarquee(progress);
    }

    readReviewData() {
      this.reviews = this.cards.map((card, reviewIndex) => {
        const template = card.querySelector('[data-pr-review-template]');
        const fragment = template ? template.content : document.createDocumentFragment();
        const read = (selector) => fragment.querySelector(selector)?.textContent.trim() || '';
        const name = this.decodeReviewText(read('[data-pr-name]'));
        const body = this.decodeReviewText(read('[data-pr-body]'));
        const media = Array.from(fragment.querySelectorAll('[data-pr-media]')).map((item, localIndex) => {
          const url = item.dataset.prMediaUrl || '';
          const thumbnail = item.dataset.prMediaThumb || url;
          const rawType = String(item.dataset.prMediaType || '1').toLowerCase();
          const type = rawType === 'video' || rawType === '2' ? 2 : 1;
          const configuredPlayerUrl = item.dataset.prMediaPlayerUrl || '';
          const playerUrl = this.getCloudflareStreamPlayerUrl(configuredPlayerUrl)
            || this.getCloudflareStreamPlayerUrl(item.dataset.prMediaSrc)
            || this.getCloudflareStreamPlayerUrl(item.dataset.prMediaOriginalUrl)
            || this.getCloudflareStreamPlayerUrl(url)
            || this.getCloudflareStreamPlayerUrl(thumbnail)
            || this.normalizeMediaUrl(configuredPlayerUrl);
          const videoSources = type === 2
            ? this.getVideoSourceCandidates([
              item.dataset.prMediaSrc,
              item.dataset.prMediaOriginalUrl,
              url
            ], thumbnail)
            : [];
          return {
            reviewIndex,
            localIndex,
            url: type === 2 ? (videoSources[0] || playerUrl || url) : url,
            fallbackUrl: type === 2
              ? (videoSources[1] || this.getOriginalMediaUrl(videoSources[0] || url))
              : this.getOriginalMediaUrl(url),
            thumbnail,
            thumbnailFallbacks: [this.getOriginalMediaUrl(thumbnail)],
            type,
            videoSources,
            playerUrl,
            alt: item.dataset.prMediaAlt || 'Customer photo'
          };
        });

        return {
          index: reviewIndex,
          id: read('[data-pr-id]') || String(reviewIndex),
          name,
          initial: this.decodeReviewText(read('[data-pr-initial]')) || name.slice(0, 1).toUpperCase(),
          body,
          date: this.decodeReviewText(read('[data-pr-date]')),
          variant: this.decodeReviewText(read('[data-pr-variant]')),
          variantUrl: read('[data-pr-variant-url]'),
          verified: read('[data-pr-verified]') === 'true',
          rating: Number(card.dataset.prRating || 5),
          starsMarkup: card.querySelector('.pr-stars')?.innerHTML || '',
          helpfulCount: this.parseHelpfulCount(read('[data-pr-helpful-count]')),
          media
        };
      });
      this.reviews.forEach((review) => {
        this.updateReviewMediaLayout(review);
        this.renderReviewCopy(review, false);
      });
    }

    decodeReviewText(value) {
      let decoded = String(value || '');

      for (let pass = 0; pass < 3; pass += 1) {
        const parsed = new DOMParser().parseFromString(decoded, 'text/html');
        const next = parsed.body?.textContent || '';
        if (next === decoded) break;
        decoded = next;
      }

      return decoded
        .replace(/\u00e2\u20ac\u02dc/g, '‘')
        .replace(/\u00e2\u20ac\u2122/g, '’')
        .replace(/\u00e2\u20ac\u0153/g, '“')
        .replace(/\u00e2\u20ac\u009d/g, '”')
        .replace(/\u00e2\u20ac\u201c/g, '–')
        .replace(/\u00e2\u20ac\u201d/g, '-')
        .replace(/\u00e2\u0080\u0098/g, '‘')
        .replace(/\u00e2\u0080\u0099/g, '’')
        .replace(/\u00e2\u0080\u009c/g, '“')
        .replace(/\u00e2\u0080\u009d/g, '”')
        .replace(/\u00e2\u0080\u0093/g, '–')
        .replace(/\u00e2\u0080\u0094/g, '-')
        .replace(/\u00c2\u00a0/g, ' ')
        .replace(/\u00a0/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    }

    parseHelpfulCount(value) {
      const count = Number(value);
      return Number.isFinite(count) ? Math.max(0, Math.floor(count)) : 0;
    }

    getHelpfulStorageKey() {
      const shopId = this.section.dataset.prShopId || 'shop';
      const productId = this.section.dataset.prProductId || 'product';
      return `${HELPFUL_STORAGE_PREFIX}:${shopId}:${productId}`;
    }

    readHelpfulReviews() {
      try {
        const stored = JSON.parse(window.localStorage.getItem(this.helpfulStorageKey) || '[]');
        if (!Array.isArray(stored)) return new Set();
        return new Set(stored.map((reviewId) => String(reviewId)));
      } catch (error) {
        return new Set();
      }
    }

    persistHelpfulReviews() {
      try {
        const reviewIds = Array.from(this.helpfulReviews);
        if (reviewIds.length) window.localStorage.setItem(this.helpfulStorageKey, JSON.stringify(reviewIds));
        else window.localStorage.removeItem(this.helpfulStorageKey);
      } catch (error) {
        // Keep the button usable when browser storage is unavailable.
      }
    }

    publishHelpfulEvent(review, isHelpful) {
      const eventData = {
        review_id: String(review.id),
        product_id: String(this.section.dataset.prProductId || ''),
        helpful: isHelpful,
        displayed_count: review.helpfulCount + (isHelpful ? 1 : 0)
      };

      try {
        const publishResult = window.Shopify?.analytics?.publish?.('aykah:review_helpful_changed', eventData);
        publishResult?.catch?.(() => {});
      } catch (error) {
        // The saved UI state should not depend on an analytics subscriber.
      }

      window.dispatchEvent(new CustomEvent('aykah:review-helpful-changed', { detail: eventData }));
    }

    renderReviewCopy(review, replaceBody = true) {
      const cards = this.section.querySelectorAll(`[data-pr-card][data-pr-review-index="${review.index}"]`);
      cards.forEach((card) => {
        card.dataset.prReviewId = String(review.id);
        card.dataset.prRating = String(review.rating);

        const openButton = card.querySelector('[data-pr-open-review]');
        const stars = card.querySelector('.pr-stars');
        const body = card.querySelector('.pr-card-text');
        const name = card.querySelector('.pr-author-name');
        const avatar = card.querySelector('.pr-avatar');
        let date = card.querySelector('.pr-author-date');
        let variant = card.querySelector('.pr-card-variant');

        if (openButton) openButton.setAttribute('aria-label', `Read ${review.name}'s full review`);
        if (stars) stars.innerHTML = review.starsMarkup;
        if (body) body.textContent = replaceBody ? review.body : this.decodeReviewText(body.textContent);
        if (name) name.textContent = review.name;
        if (avatar) avatar.textContent = review.initial;

        if (!date && review.date) {
          date = document.createElement('span');
          date.className = 'pr-author-date';
          card.querySelector('.pr-author-copy')?.appendChild(date);
        }
        if (date) {
          date.textContent = review.date;
          date.hidden = !review.date;
        }

        if (!variant && review.variant) {
          variant = document.createElement('p');
          variant.className = 'pr-card-variant';
          card.querySelector('.pr-card-author')?.before(variant);
        }
        if (variant) {
          variant.textContent = review.variant;
          variant.hidden = !review.variant;
        }
      });
    }

    normalizeReviewText(value) {
      return this.decodeReviewText(value).toLowerCase();
    }

    getOriginalMediaUrl(value) {
      const source = String(value || '');
      if (!source) return '';
      try {
        const url = new URL(source, window.location.href);
        url.searchParams.delete('x-oss-process');
        return url.toString();
      } catch (error) {
        return source;
      }
    }

    normalizeMediaUrl(value) {
      const source = String(value || '').trim();
      if (!source) return '';

      try {
        const url = new URL(source, window.location.href);
        if (url.protocol !== 'https:' && url.protocol !== 'http:') return '';
        return url.toString();
      } catch (error) {
        return '';
      }
    }

    isCloudflareStreamUrl(value) {
      const source = this.normalizeMediaUrl(value);
      if (!source) return false;

      try {
        const hostname = new URL(source).hostname.toLowerCase();
        return hostname === 'videodelivery.net'
          || hostname.endsWith('.videodelivery.net')
          || hostname === 'cloudflarestream.com'
          || hostname.endsWith('.cloudflarestream.com');
      } catch (error) {
        return false;
      }
    }

    getCloudflareStreamPlayerUrl(value) {
      const source = this.normalizeMediaUrl(value);
      if (!source || !this.isCloudflareStreamUrl(source)) return '';

      try {
        const url = new URL(source);
        const pathParts = url.pathname.split('/').filter(Boolean);
        if (!pathParts.length) return '';

        if (url.hostname.toLowerCase() === 'iframe.videodelivery.net') {
          url.pathname = `/${pathParts[0]}`;
        } else if (url.hostname.toLowerCase().endsWith('.videodelivery.net')) {
          url.hostname = 'iframe.videodelivery.net';
          url.pathname = `/${pathParts[0]}`;
        } else {
          url.pathname = `/${pathParts[0]}/iframe`;
        }

        url.search = '';
        url.hash = '';
        return url.toString();
      } catch (error) {
        return '';
      }
    }

    isLikelyImageUrl(value) {
      const source = String(value || '').toLowerCase();
      return /\.(?:avif|gif|jpe?g|png|svg|webp)(?:$|[?#])/.test(source)
        || /(?:image\/|video\/snapshot|x-oss-process=image)/.test(source);
    }

    isLikelyDirectVideoUrl(value) {
      const source = String(value || '').toLowerCase();
      return /\.(?:m4v|mov|mp4|ogv|webm)(?:$|[?#])/.test(source)
        || /(?:response-content-type|content-type)=video(?:%2f|\/)/.test(source);
    }

    getVideoSourceCandidates(values, thumbnail = '') {
      const thumbnailUrl = this.normalizeMediaUrl(thumbnail);
      const expanded = [];

      values.filter(Boolean).forEach((value) => {
        expanded.push(value, this.getOriginalMediaUrl(value));
      });

      if (thumbnail) expanded.push(this.getOriginalMediaUrl(thumbnail));

      const sources = [...new Set(expanded.map((value) => this.normalizeMediaUrl(value)).filter(Boolean))]
        .filter((value) => (
          value !== thumbnailUrl
          && !this.isLikelyImageUrl(value)
          && !this.isCloudflareStreamUrl(value)
        ));

      return sources.sort((left, right) => (
        Number(this.isLikelyDirectVideoUrl(right)) - Number(this.isLikelyDirectVideoUrl(left))
      ));
    }

    setMediaSourceWithFallback(mediaElement, sources, onFinalError) {
      const queue = [...new Set(sources.filter(Boolean).map((source) => String(source)))];
      if (!queue.length) {
        onFinalError?.();
        return;
      }

      let sourceIndex = 0;
      mediaElement.addEventListener('error', () => {
        sourceIndex += 1;
        if (sourceIndex < queue.length) mediaElement.src = queue[sourceIndex];
        else onFinalError?.();
      });
      mediaElement.src = queue[sourceIndex];
    }

    clampRating(value) {
      const rating = Math.round(Number(value));
      return Number.isFinite(rating) ? Math.min(Math.max(rating, 1), 5) : 5;
    }

    getStarsMarkup(rating) {
      const safeRating = this.clampRating(rating);
      const template = this.section.querySelector(`[data-pr-stars-template="${safeRating}"]`);
      if (template) return template.innerHTML.trim();
      return this.reviews.find((review) => review.rating === safeRating)?.starsMarkup
        || this.reviews[0]?.starsMarkup
        || '';
    }

    formatReviewDate(value) {
      const rawValue = String(value || '').trim();
      if (!rawValue) return '';

      const dateParts = rawValue.match(/^(\d{4})-(\d{1,2})/);
      let date;
      if (dateParts) {
        date = new Date(Date.UTC(Number(dateParts[1]), Number(dateParts[2]) - 1, 1));
      } else {
        date = new Date(rawValue);
      }
      if (Number.isNaN(date.getTime())) return this.decodeReviewText(rawValue);

      try {
        return new Intl.DateTimeFormat(document.documentElement.lang || 'en-CA', {
          month: 'short',
          year: 'numeric',
          timeZone: 'UTC'
        }).format(date);
      } catch (error) {
        return `${date.toLocaleString('en', { month: 'short', timeZone: 'UTC' })} ${date.getUTCFullYear()}`;
      }
    }

    getTrustooIdentity(review) {
      if (review?.id !== undefined && review?.id !== null && String(review.id)) {
        return `id:${String(review.id)}`;
      }
      return `content:${this.normalizeReviewText(review?.author)}|${this.normalizeReviewText(review?.content)}`;
    }

    buildVariantUrl(variantId) {
      if (variantId === undefined || variantId === null || String(variantId) === '') return '';
      const url = new URL(window.location.href);
      url.search = '';
      url.hash = 'product-hero';
      url.searchParams.set('variant', String(variantId));
      return `${url.pathname}${url.search}${url.hash}`;
    }

    normalizeTrustooResource(resource, review) {
      if (!resource || resource.is_published === 0 || resource.is_published === false) return null;

      const rawType = String(resource.resource_type || resource.type || resource.media_type || 1).toLowerCase();
      const type = rawType === '2' || rawType === 'video' ? 2 : 1;
      const thumbnail = String(
        resource.card_thumbnail_url
        || resource.thumb_src
        || resource.thumbnail_url
        || resource.thumb_url
        || resource.thumbnail
        || ''
      );
      const configuredPlayerUrl = resource.video_player_url || resource.player_url || resource.embed_url;
      const playerUrl = type === 2
        ? (
          this.getCloudflareStreamPlayerUrl(configuredPlayerUrl)
          || this.getCloudflareStreamPlayerUrl(resource.video_src)
          || this.getCloudflareStreamPlayerUrl(resource.video_url)
          || this.getCloudflareStreamPlayerUrl(resource.original_url)
          || this.getCloudflareStreamPlayerUrl(resource.file_url)
          || this.getCloudflareStreamPlayerUrl(resource.url)
          || this.getCloudflareStreamPlayerUrl(resource.src)
          || this.getCloudflareStreamPlayerUrl(thumbnail)
          || this.normalizeMediaUrl(configuredPlayerUrl)
        )
        : '';
      const videoSources = type === 2
        ? this.getVideoSourceCandidates([
          resource.video_src,
          resource.video_url,
          resource.original_url,
          resource.file_url,
          resource.download_url,
          resource.url,
          resource.src
        ], thumbnail)
        : [];
      const source = type === 2
        ? (videoSources[0] || playerUrl)
        : (resource.src || resource.url);
      if (!source) return null;

      const originalUrl = this.getOriginalMediaUrl(source);
      const displayThumbnail = thumbnail || (type === 1 ? String(resource.src || source) : '');
      const hasInvalidImageDimensions = type === 1 && (
        (resource.width !== undefined && Number(resource.width) <= 0)
        || (resource.height !== undefined && Number(resource.height) <= 0)
      );

      return {
        reviewIndex: review.index,
        localIndex: 0,
        url: hasInvalidImageDimensions ? originalUrl : String(source),
        fallbackUrl: type === 2 ? (videoSources[1] || originalUrl) : originalUrl,
        thumbnail: hasInvalidImageDimensions ? originalUrl : displayThumbnail,
        thumbnailFallbacks: type === 2
          ? [resource.thumb_src, resource.thumbnail_url, resource.card_thumbnail_url]
          : [resource.thumb_src, resource.src, originalUrl],
        type,
        videoSources,
        playerUrl,
        width: Number(resource.width || resource.video_width || 0),
        height: Number(resource.height || resource.video_height || 0),
        alt: `${type === 2 ? 'Customer video' : 'Customer photo'} from ${review.name}`
      };
    }

    reindexReviewMedia() {
      this.reviews.forEach((review, reviewIndex) => {
        review.media = review.media.filter((item) => item?.url).map((item, localIndex) => {
          return {
            ...item,
            reviewIndex,
            localIndex
          };
        });
      });
    }

    updateReviewMediaLayout(review) {
      const cards = this.section.querySelectorAll(`[data-pr-card][data-pr-review-index="${review.index}"]`);
      cards.forEach((card) => card.classList.toggle('pr-card--has-media', review.media.length > 0));
    }

    renderReviewMedia(review) {
      const cards = this.section.querySelectorAll(`[data-pr-card][data-pr-review-index="${review.index}"]`);
      cards.forEach((card) => {
        const container = card.querySelector('.pr-card-media');
        if (!container) return;

        container.replaceChildren();
        card.classList.toggle('pr-card--has-media', review.media.length > 0);
        review.media.slice(0, 3).forEach((photo, localIndex) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = `pr-card-thumb${photo.type === 2 ? ' pr-card-thumb--video' : ''}`;
          button.dataset.prOpenPhoto = '';
          button.dataset.prLocalPhoto = String(localIndex);
          button.setAttribute('aria-label', `Open customer ${photo.type === 2 ? 'video' : 'photo'} ${localIndex + 1} from ${review.name}`);
          if (card.closest('[data-pr-clone]')) button.tabIndex = -1;

          const image = document.createElement('img');
          image.alt = photo.alt;
          image.width = 108;
          image.height = 108;
          image.loading = 'lazy';
          image.decoding = 'async';
          this.setMediaSourceWithFallback(
            image,
            [photo.thumbnail, ...(photo.thumbnailFallbacks || []), photo.fallbackUrl],
            () => {
              button.remove();
              if (!container.querySelector('[data-pr-open-photo]')) {
                card.classList.remove('pr-card--has-media');
              }
            }
          );
          button.appendChild(image);

          if (localIndex === 2 && review.media.length > 3) {
            const remainingCount = document.createElement('span');
            remainingCount.className = 'pr-card-thumb-count';
            remainingCount.textContent = `+${review.media.length - 3}`;
            remainingCount.setAttribute('aria-hidden', 'true');
            button.appendChild(remainingCount);
            button.setAttribute(
              'aria-label',
              `Open customer ${photo.type === 2 ? 'video' : 'photo'} ${localIndex + 1} from ${review.name}. ${review.media.length - 3} more in this review.`
            );
          }

          container.appendChild(button);
        });
      });
    }

    createReviewFromTrustoo(remoteReview, reviewIndex) {
      const name = this.decodeReviewText(remoteReview?.author) || 'Verified buyer';
      const body = this.decodeReviewText(remoteReview?.content);
      if (!body) return null;

      const rating = this.clampRating(remoteReview?.star ?? remoteReview?.rating);
      const variant = this.decodeReviewText(remoteReview?.variant_title || remoteReview?.variant_name);
      const review = {
        index: reviewIndex,
        id: String(remoteReview?.id ?? `trustoo-${reviewIndex}`),
        name,
        initial: name.slice(0, 1).toUpperCase(),
        body,
        date: this.formatReviewDate(remoteReview?.commented_at || remoteReview?.created_at),
        variant,
        variantUrl: this.buildVariantUrl(remoteReview?.variant_id),
        verified: Number(remoteReview?.verified_badge || 0) === 1
          || remoteReview?.is_verified === true
          || Number(remoteReview?.is_verified || 0) === 1,
        rating,
        starsMarkup: this.getStarsMarkup(rating),
        helpfulCount: this.parseHelpfulCount(remoteReview?.likes_count ?? remoteReview?.helpful_count),
        media: []
      };

      review.media = Array.isArray(remoteReview?.resources)
        ? remoteReview.resources.map((resource) => this.normalizeTrustooResource(resource, review)).filter(Boolean)
        : [];
      return review.media.length ? review : null;
    }

    createRemoteReviewCard(review) {
      const card = document.createElement('article');
      card.className = 'pr-card';
      if (this.isPhotoFirstV2) card.setAttribute('role', 'listitem');
      card.dataset.prCard = '';
      card.dataset.prReviewId = String(review.id);
      card.dataset.prReviewIndex = String(review.index);
      card.dataset.prRating = String(review.rating);

      const openButton = document.createElement('button');
      openButton.type = 'button';
      openButton.className = 'pr-card-open';
      openButton.dataset.prOpenReview = '';
      openButton.setAttribute('aria-label', `Read ${review.name}'s full review`);
      card.appendChild(openButton);

      const stars = document.createElement('div');
      stars.className = 'pr-stars';
      stars.innerHTML = review.starsMarkup;
      card.appendChild(stars);

      const body = document.createElement('p');
      body.className = 'pr-card-text';
      body.textContent = review.body;
      card.appendChild(body);

      const media = document.createElement('div');
      media.className = 'pr-card-media';
      media.setAttribute('aria-label', `Customer media from ${review.name}`);
      card.appendChild(media);

      if (review.variant) {
        const variant = document.createElement('p');
        variant.className = 'pr-card-variant';
        variant.textContent = review.variant;
        card.appendChild(variant);
      }

      const author = document.createElement('div');
      author.className = 'pr-card-author';

      const avatar = document.createElement('span');
      avatar.className = 'pr-avatar';
      avatar.setAttribute('aria-hidden', 'true');
      avatar.textContent = review.initial;
      author.appendChild(avatar);

      const authorCopy = document.createElement('span');
      authorCopy.className = 'pr-author-copy';
      const name = document.createElement('span');
      name.className = 'pr-author-name';
      name.textContent = review.name;
      authorCopy.appendChild(name);
      if (review.date) {
        const date = document.createElement('span');
        date.className = 'pr-author-date';
        date.textContent = review.date;
        authorCopy.appendChild(date);
      }
      author.appendChild(authorCopy);
      card.appendChild(author);

      return card;
    }

    hydrateReviewFromTrustoo(review, remoteReview) {
      const remoteId = remoteReview?.id !== undefined && remoteReview?.id !== null
        ? String(remoteReview.id)
        : '';
      const remoteName = this.decodeReviewText(remoteReview?.author);
      const remoteBody = this.decodeReviewText(remoteReview?.content);
      const remoteDate = this.formatReviewDate(remoteReview?.commented_at || remoteReview?.created_at);
      const remoteVariant = this.decodeReviewText(remoteReview?.variant_title || remoteReview?.variant_name);
      const remoteRating = this.clampRating(remoteReview?.star ?? remoteReview?.rating ?? review.rating);

      if (remoteId) review.id = remoteId;
      if (remoteName) {
        review.name = remoteName;
        review.initial = remoteName.slice(0, 1).toUpperCase();
      }
      if (remoteBody) review.body = remoteBody;
      if (remoteDate) review.date = remoteDate;
      if (remoteVariant) review.variant = remoteVariant;
      if (!review.variantUrl && remoteReview?.variant_id) {
        review.variantUrl = this.buildVariantUrl(remoteReview.variant_id);
      }

      review.rating = remoteRating;
      review.starsMarkup = this.getStarsMarkup(remoteRating);
      review.helpfulCount = this.parseHelpfulCount(remoteReview?.likes_count ?? remoteReview?.helpful_count);
      review.verified = review.verified
        || Number(remoteReview?.verified_badge || 0) === 1
        || remoteReview?.is_verified === true
        || Number(remoteReview?.is_verified || 0) === 1;

      if (Array.isArray(remoteReview?.resources) && remoteReview.resources.length) {
        const media = remoteReview.resources
          .map((resource) => this.normalizeTrustooResource(resource, review))
          .filter(Boolean);
        if (media.length) review.media = media;
      }
    }

    async fetchTrustooPage(shopId, productId, page) {
      const query = new URLSearchParams({
        shop_id: shopId,
        product_id: productId,
        limit: '50',
        page: String(page),
        scene: '3',
        is_show_all: '0',
        no_empty: '1'
      });
      const response = await window.fetch(`${TRUSTOO_REVIEWS_ENDPOINT}?${query}`, {
        credentials: 'omit',
        mode: 'cors'
      });
      if (!response.ok) throw new Error(`Trustoo page ${page} failed`);

      const payload = await response.json();
      return {
        reviews: Array.isArray(payload?.data?.list) ? payload.data.list : [],
        totalPages: Math.max(1, Number(payload?.data?.page?.total_page || 1))
      };
    }

    rebuildReviewRail(activeReview) {
      this.marqueeAnimation?.cancel();
      this.marqueeAnimation = null;
      this.track.querySelector('[data-pr-clone]')?.remove();

      this.cards = Array.from(this.primarySet.querySelectorAll('[data-pr-card]'));
      this.randomizeReviewOrder();
      this.updateReviewCountState();

      const reviewBuckets = new Map();
      this.reviews.forEach((review) => {
        const key = String(review.id);
        if (!reviewBuckets.has(key)) reviewBuckets.set(key, []);
        reviewBuckets.get(key).push(review);
      });

      this.reviews = this.cards.map((card) => {
        const bucket = reviewBuckets.get(String(card.dataset.prReviewId)) || [];
        return bucket.shift();
      }).filter(Boolean);

      this.cards.forEach((card, reviewIndex) => {
        card.dataset.prReviewIndex = String(reviewIndex);
      });
      this.reviews.forEach((review, reviewIndex) => {
        review.index = reviewIndex;
      });
      this.reindexReviewMedia();
      this.marqueeDuration = this.marqueeSecondsPerCard * Math.max(this.cards.length, 1) * 1000;
      this.prepareMarquee();
      this.reviews.forEach((review) => {
        this.renderReviewCopy(review);
        this.renderReviewMedia(review);
      });
      this.activeReviewIndex = Math.max(0, this.reviews.indexOf(activeReview));
      this.startMarquee();
      this.updateTrackControls();
      if (this.isPhotoFirstV2) this.refreshV2();
    }

    async loadTrustooData() {
      if (this.section.dataset.prTrustooEnabled !== 'true' || typeof window.fetch !== 'function') return;

      const shopId = this.section.dataset.prShopId;
      const productId = this.section.dataset.prProductId;
      if (!shopId || !productId) return;

      try {
        const firstPage = await this.fetchTrustooPage(shopId, productId, 1);
        const remainingPages = Array.from(
          { length: Math.max(0, firstPage.totalPages - 1) },
          (_, pageIndex) => pageIndex + 2
        );
        const remainingResults = await Promise.allSettled(
          remainingPages.map((page) => this.fetchTrustooPage(shopId, productId, page))
        );
        const pageReviews = remainingResults
          .filter((result) => result.status === 'fulfilled')
          .flatMap((result) => result.value.reviews);

        const seenRemoteReviews = new Set();
        const remoteReviews = [...firstPage.reviews, ...pageReviews].filter((review) => {
          const identity = this.getTrustooIdentity(review);
          if (seenRemoteReviews.has(identity)) return false;
          seenRemoteReviews.add(identity);
          return true;
        });
        if (!remoteReviews.length) return;

        const byId = new Map();
        const byContent = new Map();
        remoteReviews.forEach((review) => {
          if (review?.id !== undefined && review?.id !== null) byId.set(String(review.id), review);
          const contentKey = `${this.normalizeReviewText(review?.author)}|${this.normalizeReviewText(review?.content)}`;
          if (contentKey !== '|' && !byContent.has(contentKey)) byContent.set(contentKey, review);
        });

        const activeReview = this.reviews[this.activeReviewIndex];
        const matchedRemoteReviews = new Set();
        this.reviews.forEach((review) => {
          const contentKey = `${this.normalizeReviewText(review.name)}|${this.normalizeReviewText(review.body)}`;
          const remoteReview = byId.get(String(review.id)) || byContent.get(contentKey);
          if (!remoteReview) return;
          matchedRemoteReviews.add(this.getTrustooIdentity(remoteReview));
          this.hydrateReviewFromTrustoo(review, remoteReview);
          this.renderReviewCopy(review);
        });

        const existingIds = new Set(this.reviews.map((review) => String(review.id)));
        const existingContent = new Set(this.reviews.map((review) => (
          `${this.normalizeReviewText(review.name)}|${this.normalizeReviewText(review.body)}`
        )));
        let addedReviewCount = 0;

        remoteReviews.forEach((remoteReview) => {
          const remoteIdentity = this.getTrustooIdentity(remoteReview);
          const remoteId = remoteReview?.id !== undefined && remoteReview?.id !== null
            ? String(remoteReview.id)
            : '';
          const contentKey = `${this.normalizeReviewText(remoteReview?.author)}|${this.normalizeReviewText(remoteReview?.content)}`;
          if (
            matchedRemoteReviews.has(remoteIdentity)
            || (remoteId && existingIds.has(remoteId))
            || existingContent.has(contentKey)
          ) return;

          const review = this.createReviewFromTrustoo(remoteReview, this.reviews.length);
          if (!review) return;

          this.reviews.push(review);
          this.primarySet.appendChild(this.createRemoteReviewCard(review));
          existingIds.add(String(review.id));
          existingContent.add(contentKey);
          addedReviewCount += 1;
        });

        if (addedReviewCount) {
          this.rebuildReviewRail(activeReview);
        } else {
          this.reindexReviewMedia();
          this.reviews.forEach((review) => {
            this.renderReviewCopy(review);
            this.renderReviewMedia(review);
          });
        }

        if (this.isPhotoFirstV2) this.refreshV2();
        if (!this.modal.hidden) this.showReview(this.activeReviewIndex, true);
      } catch (error) {
        // Keep the Liquid-rendered review panel intact if the optional media request fails.
      }
    }

    cacheModalElements() {
      this.mainPhoto = this.modal.querySelector('[data-pr-main-photo]');
      this.mainPhotoMedia = this.modal.querySelector('[data-pr-main-photo-media]');
      this.photoPrevious = this.modal.querySelector('[data-pr-photo-prev]');
      this.photoNext = this.modal.querySelector('[data-pr-photo-next]');
      this.thumbStrip = this.modal.querySelector('[data-pr-modal-thumbs]');
      this.photoCount = this.modal.querySelector('[data-pr-photo-count]');
      this.stars = this.modal.querySelector('[data-pr-modal-stars]');
      this.body = this.modal.querySelector('[data-pr-modal-body]');
      this.variant = this.modal.querySelector('[data-pr-modal-variant]');
      this.avatar = this.modal.querySelector('[data-pr-modal-avatar]');
      this.name = this.modal.querySelector('[data-pr-modal-name]');
      this.date = this.modal.querySelector('[data-pr-modal-date]');
      this.shopLink = this.modal.querySelector('[data-pr-shop-link]');
      this.shopHint = this.modal.querySelector('[data-pr-shop-hint]');
      this.helpfulButton = this.modal.querySelector('[data-pr-helpful]');
      this.helpfulLabel = this.modal.querySelector('[data-pr-helpful-label]');
      this.helpfulCount = this.modal.querySelector('[data-pr-helpful-count]');
      this.helpfulDefaultText = this.helpfulLabel?.textContent.trim() || 'Helpful';
      this.reviewPrevious = this.modal.querySelector('[data-pr-review-prev]');
      this.reviewNext = this.modal.querySelector('[data-pr-review-next]');
      this.modalDetails = this.modal.querySelector('.pr-modal-details');
    }

    cacheV2Elements() {
      const one = (selector) => this.section.querySelector(selector);
      this.v2Elements = {
        average: one('[data-pr-v2-average]'),
        summaryStars: one('[data-pr-v2-summary-stars]'),
        total: one('[data-pr-v2-total]'),
        writeReview: one('[data-pr-write-review]'),
        mediaShell: one('[data-pr-v2-media-shell]'),
        mediaStrip: one('[data-pr-v2-media-strip]'),
        mediaPrevious: one('[data-pr-media-prev]'),
        mediaNext: one('[data-pr-media-next]'),
        mediaTabs: Array.from(this.section.querySelectorAll('[data-pr-media-tab]')),
        mediaCounts: Array.from(this.section.querySelectorAll('[data-pr-media-count]')),
        playIconTemplate: one('[data-pr-play-icon-template]'),
        search: one('[data-pr-review-search]'),
        sort: one('[data-pr-review-sort]'),
        sortDropdown: one('[data-pr-sort-dropdown]'),
        sortButton: one('[data-pr-sort-button]'),
        sortLabel: one('[data-pr-sort-label]'),
        sortMenu: one('[data-pr-sort-menu]'),
        sortOptions: Array.from(this.section.querySelectorAll('[data-pr-sort-option]')),
        ratingButtons: Array.from(this.section.querySelectorAll('[data-pr-rating-filter]')),
        ratingMobile: one('[data-pr-rating-mobile]'),
        ratingSelect: one('[data-pr-rating-select]'),
        ratingCounts: Array.from(this.section.querySelectorAll('[data-pr-rating-count]')),
        results: one('[data-pr-filter-results]'),
        resultCount: one('[data-pr-filter-result-count]'),
        resultDescription: one('[data-pr-filter-description]'),
        clearFilters: one('[data-pr-clear-filters]'),
        empty: one('[data-pr-filter-empty]'),
        reviewShell: one('.pr-v2-review-shell'),
        showMore: one('[data-pr-show-more]')
      };
    }

    getV2PageSize() {
      if (window.innerWidth <= 767) return 1;

      const availableWidth = Number(
        this.v2Elements?.reviewShell?.clientWidth
        || this.section.querySelector('.pr-v2-content')?.clientWidth
        || window.innerWidth
        || 1240
      );
      const minimumCardWidth = 280;
      const gap = 16;
      const columns = Math.floor((availableWidth + gap) / (minimumCardWidth + gap));
      return Math.max(1, Math.min(4, columns));
    }

    refreshV2() {
      if (!this.isPhotoFirstV2 || !this.v2Elements) return;

      this.cards = Array.from(this.primarySet.querySelectorAll('[data-pr-card]'));
      this.cards.forEach((card) => card.setAttribute('role', 'listitem'));
      this.reindexReviewMedia();
      this.v2AllMedia = this.reviews.flatMap((review) => review.media);

      const reviewCount = this.reviews.length;
      const average = reviewCount
        ? this.reviews.reduce((sum, review) => sum + this.clampRating(review.rating), 0) / reviewCount
        : 0;
      if (this.v2Elements.average) this.v2Elements.average.textContent = average.toFixed(2);
      if (this.v2Elements.total) {
        this.v2Elements.total.textContent = `${reviewCount} ${reviewCount === 1 ? 'review' : 'reviews'}`;
      }
      if (this.v2Elements.summaryStars) {
        this.v2Elements.summaryStars.innerHTML = this.getStarsMarkup(Math.round(average || 5));
      }

      const videos = this.v2AllMedia.filter((item) => item.type === 2).length;
      const mediaCounts = { all: this.v2AllMedia.length, videos };
      this.v2Elements.mediaCounts.forEach((element) => {
        element.textContent = String(mediaCounts[element.dataset.prMediaCount] || 0);
      });
      this.v2Elements.mediaTabs.forEach((tab) => {
        const count = mediaCounts[tab.dataset.prMediaTab] || 0;
        tab.disabled = count === 0;
      });

      if (!mediaCounts[this.v2State.mediaType]) this.v2State.mediaType = 'all';
      this.section.classList.toggle('is-v2-no-media', this.v2AllMedia.length === 0);
      this.updateV2MediaTabs();
      this.renderV2MediaStrip();

      const ratingCounts = this.reviews.reduce((counts, review) => {
        const rating = this.clampRating(review.rating);
        counts[rating] += 1;
        return counts;
      }, { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 });
      this.v2Elements.ratingCounts.forEach((element) => {
        element.textContent = String(ratingCounts[Number(element.dataset.prRatingCount)] || 0);
      });

      this.applyV2Filters({ resetVisible: false, animateLayout: false });
    }

    updateV2MediaTabs() {
      this.v2Elements.mediaTabs.forEach((tab) => {
        const isActive = tab.dataset.prMediaTab === this.v2State.mediaType;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        tab.tabIndex = isActive ? 0 : -1;
      });
    }

    getV2MediaItems() {
      if (this.v2State.mediaType === 'videos') {
        return this.v2AllMedia.filter((item) => item.type === 2);
      }
      return this.v2AllMedia;
    }

    renderV2MediaStrip() {
      const strip = this.v2Elements.mediaStrip;
      if (!strip) return;

      strip.replaceChildren();
      this.getV2MediaItems().forEach((media) => {
        const item = document.createElement('div');
        item.className = 'pr-v2-media-item';
        item.setAttribute('role', 'listitem');

        const button = document.createElement('button');
        button.type = 'button';
        button.className = `pr-v2-media-button${media.type === 2 ? ' pr-v2-media-button--video' : ''}`;
        button.dataset.prV2MediaItem = '';
        button.dataset.prReviewIndex = String(media.reviewIndex);
        button.dataset.prLocalPhoto = String(media.localIndex);
        button.setAttribute('aria-label', `Open ${media.type === 2 ? 'customer video' : 'customer photo'} from ${this.reviews[media.reviewIndex]?.name || 'this review'}`);

        const image = document.createElement('img');
        image.alt = '';
        image.width = 448;
        image.height = 400;
        image.loading = 'lazy';
        image.decoding = 'async';
        const sources = [media.thumbnail, ...(media.thumbnailFallbacks || []), media.fallbackUrl];
        if (sources.some(Boolean)) {
          this.setMediaSourceWithFallback(image, sources, () => {
            if (media.type === 1) item.remove();
            else image.remove();
            this.updateV2MediaControls();
          });
          button.appendChild(image);
        }
        if (media.type === 2 && this.v2Elements.playIconTemplate) {
          button.appendChild(this.v2Elements.playIconTemplate.content.cloneNode(true));
        }

        item.appendChild(button);
        strip.appendChild(item);
      });

      strip.scrollLeft = 0;
      window.requestAnimationFrame(() => this.updateV2MediaControls());
    }

    updateV2MediaControls() {
      const strip = this.v2Elements?.mediaStrip;
      const previous = this.v2Elements?.mediaPrevious;
      const next = this.v2Elements?.mediaNext;
      if (!strip || !previous || !next) return;
      const maximum = Math.max(0, strip.scrollWidth - strip.clientWidth);
      previous.disabled = maximum <= 1 || strip.scrollLeft <= 2;
      next.disabled = maximum <= 1 || strip.scrollLeft >= maximum - 2;
    }

    scrollV2Media(direction) {
      const strip = this.v2Elements?.mediaStrip;
      const item = strip?.querySelector('.pr-v2-media-item');
      if (!strip || !item) return;
      const styles = getComputedStyle(strip);
      const gap = Number.parseFloat(styles.columnGap || styles.gap || 0) || 0;
      strip.scrollBy({
        left: direction * (item.getBoundingClientRect().width + gap),
        behavior: REDUCED_MOTION.matches ? 'auto' : 'smooth'
      });
    }

    getV2ReviewDateValue(review) {
      const value = Date.parse(review.date || '');
      return Number.isNaN(value) ? 0 : value;
    }

    getV2FilteredReviews() {
      const query = this.normalizeReviewText(this.v2State.search);
      const matches = this.reviews.filter((review) => {
        if (this.v2State.rating && this.clampRating(review.rating) !== this.v2State.rating) return false;
        if (!query) return true;
        const searchable = this.normalizeReviewText([
          review.body,
          review.name,
          review.variant,
          review.date
        ].filter(Boolean).join(' '));
        return searchable.includes(query);
      });

      if (this.v2State.sort === 'highest') {
        matches.sort((left, right) => this.clampRating(right.rating) - this.clampRating(left.rating));
      } else if (this.v2State.sort === 'lowest') {
        matches.sort((left, right) => this.clampRating(left.rating) - this.clampRating(right.rating));
      } else if (this.v2State.sort === 'recent') {
        matches.sort((left, right) => this.getV2ReviewDateValue(right) - this.getV2ReviewDateValue(left));
      }
      return matches;
    }

    hasV2Filters() {
      return Boolean(this.v2State.rating || this.v2State.search.trim());
    }

    animateV2SectionHeight(previousHeight) {
      if (
        !Number.isFinite(previousHeight)
        || REDUCED_MOTION.matches
        || typeof this.section.animate !== 'function'
      ) return;

      const nextHeight = this.section.getBoundingClientRect().height;
      if (!Number.isFinite(nextHeight) || Math.abs(nextHeight - previousHeight) < 2) return;

      const animation = this.section.animate(
        [
          { height: `${previousHeight}px` },
          { height: `${nextHeight}px` }
        ],
        {
          duration: 260,
          easing: 'cubic-bezier(.22, 1, .36, 1)'
        }
      );
      this.v2LayoutAnimation = animation;
      this.section.classList.add('is-v2-resizing');

      const finish = () => {
        if (this.v2LayoutAnimation !== animation) return;
        this.v2LayoutAnimation = null;
        this.section.classList.remove('is-v2-resizing');
      };
      animation.addEventListener?.('finish', finish, { once: true });
      animation.addEventListener?.('cancel', finish, { once: true });
    }

    isV2SortMenuOpen() {
      return this.v2Elements?.sortButton?.getAttribute('aria-expanded') === 'true';
    }

    setV2SortMenuOpen(isOpen, { focusOption = false, restoreFocus = false } = {}) {
      const elements = this.v2Elements;
      if (!elements?.sortDropdown || !elements.sortButton || !elements.sortMenu) return;

      const open = Boolean(isOpen);
      elements.sortDropdown.classList.toggle('is-open', open);
      elements.sortButton.setAttribute('aria-expanded', open ? 'true' : 'false');
      elements.sortMenu.hidden = !open;

      if (open && focusOption) {
        window.requestAnimationFrame(() => {
          const selected = elements.sortOptions?.find((option) => option.getAttribute('aria-selected') === 'true');
          (selected || elements.sortOptions?.[0])?.focus();
        });
      } else if (!open && restoreFocus) {
        elements.sortButton.focus();
      }
    }

    selectV2Sort(value, { restoreFocus = true } = {}) {
      if (!this.v2Elements?.sort || !value) return;
      this.v2Elements.sort.value = value;
      this.v2State.sort = value;
      this.setV2SortMenuOpen(false, { restoreFocus });
      this.applyV2Filters({ resetVisible: false });
    }

    moveV2SortOptionFocus(currentOption, direction) {
      const options = this.v2Elements?.sortOptions || [];
      const currentIndex = options.indexOf(currentOption);
      if (!options.length || currentIndex < 0) return;

      let nextIndex;
      if (direction === 'first') nextIndex = 0;
      else if (direction === 'last') nextIndex = options.length - 1;
      else nextIndex = (currentIndex + direction + options.length) % options.length;
      options[nextIndex].focus();
    }

    applyV2Filters({ resetVisible = true, animateLayout = true } = {}) {
      if (!this.v2Elements) return;
      if (resetVisible) this.v2State.visiblePages = 1;

      const isFiltered = this.hasV2Filters();
      const shouldAnimateLayout = animateLayout && (
        isFiltered || this.section.classList.contains('is-v2-filtered')
      );
      const previousHeight = shouldAnimateLayout
        ? this.section.getBoundingClientRect().height
        : null;
      if (shouldAnimateLayout && this.v2LayoutAnimation) {
        this.v2LayoutAnimation.cancel();
        this.v2LayoutAnimation = null;
      }

      const matches = this.getV2FilteredReviews();
      const pageSize = this.getV2PageSize();
      const visibleLimit = pageSize * Math.max(1, this.v2State.visiblePages || 1);
      const visibleMatchCount = isFiltered ? Math.min(matches.length, visibleLimit) : matches.length;
      const remainingMatchCount = isFiltered ? Math.max(0, matches.length - visibleMatchCount) : 0;
      const matchingIndices = new Set(matches.map((review) => review.index));
      const orderedReviews = [
        ...matches,
        ...this.reviews.filter((review) => !matchingIndices.has(review.index))
      ];
      orderedReviews.forEach((review) => {
        const card = this.primarySet.querySelector(`[data-pr-card][data-pr-review-index="${review.index}"]`);
        if (card) this.primarySet.appendChild(card);
      });
      this.v2DisplayedReviewIndices = matches.map((review) => review.index);

      this.reviews.forEach((review) => {
        const card = this.primarySet.querySelector(`[data-pr-card][data-pr-review-index="${review.index}"]`);
        if (!card) return;
        const matchIndex = matches.indexOf(review);
        const shouldShow = !isFiltered || (matchIndex >= 0 && matchIndex < visibleLimit);
        card.hidden = !shouldShow;
        if (shouldShow) card.removeAttribute('aria-hidden');
        else card.setAttribute('aria-hidden', 'true');
      });

      this.section.classList.toggle('is-v2-filtered', isFiltered);
      this.v2Elements.results.hidden = !isFiltered;
      this.v2Elements.empty.hidden = !isFiltered || matches.length > 0;
      if (this.v2Elements.reviewShell) this.v2Elements.reviewShell.hidden = isFiltered && matches.length === 0;
      this.v2Elements.showMore.hidden = remainingMatchCount === 0;
      this.v2Elements.showMore.disabled = remainingMatchCount === 0;
      this.v2Elements.resultCount.textContent = `${matches.length} ${matches.length === 1 ? 'review matches' : 'reviews match'}`;

      const descriptions = [];
      if (this.v2State.rating) descriptions.push(`${this.v2State.rating} star`);
      if (this.v2State.search.trim()) descriptions.push(`“${this.v2State.search.trim()}”`);
      this.v2Elements.resultDescription.textContent = descriptions.join(' · ');

      if (this.v2Elements.sortLabel && this.v2Elements.sort) {
        const selectedOption = this.v2Elements.sort.selectedOptions?.[0];
        this.v2Elements.sortLabel.textContent = selectedOption?.dataset.prSortOptionLabel
          || selectedOption?.textContent
          || 'Relevance';
      }
      this.v2Elements.sortOptions?.forEach((option) => {
        const isSelected = option.dataset.prSortOption === this.v2State.sort;
        option.classList.toggle('is-selected', isSelected);
        option.setAttribute('aria-selected', isSelected ? 'true' : 'false');
        option.tabIndex = isSelected ? 0 : -1;
      });

      this.v2Elements.ratingButtons.forEach((button) => {
        const isActive = Number(button.dataset.prRatingFilter) === this.v2State.rating;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });
      if (this.v2Elements.ratingSelect) this.v2Elements.ratingSelect.value = String(this.v2State.rating || 0);
      this.v2Elements.ratingMobile?.classList.toggle('is-active', Boolean(this.v2State.rating));

      if (isFiltered) {
        const marqueeClone = this.track.querySelector('[data-pr-clone]');
        if (marqueeClone) marqueeClone.hidden = true;
        this.pauseMarquee('v2-filter');
        this.viewport.scrollLeft = 0;
      } else {
        this.syncV2Marquee();
        this.resumeMarquee('v2-filter');
      }
      if (shouldAnimateLayout) this.animateV2SectionHeight(previousHeight);
      window.requestAnimationFrame(() => this.updateTrackControls());
    }

    clearV2Filters() {
      this.v2State.rating = 0;
      this.v2State.search = '';
      if (this.v2Elements.search) this.v2Elements.search.value = '';
      this.applyV2Filters();
    }

    navigateV2Review(direction) {
      const displayedOrder = this.v2DisplayedReviewIndices.includes(this.activeReviewIndex)
        ? this.v2DisplayedReviewIndices
        : this.reviews.map((review) => review.index);
      const currentPosition = displayedOrder.indexOf(this.activeReviewIndex);
      const nextPosition = currentPosition + direction;
      if (currentPosition < 0 || nextPosition < 0 || nextPosition >= displayedOrder.length) return;
      this.showReview(displayedOrder[nextPosition], true);
    }

    openV2WriteReview(clickEvent = null) {
      const requestEvent = new CustomEvent('aykah:write-review-requested', {
        cancelable: true,
        detail: { productId: String(this.section.dataset.prProductId || '') }
      });
      if (!window.dispatchEvent(requestEvent)) {
        clickEvent?.preventDefault();
        return;
      }

      const linkUrl = String(this.v2Elements.writeReview?.getAttribute?.('href') || '').trim();
      const configuredUrl = linkUrl || String(this.section.dataset.prWriteReviewUrl || '').trim();

      // Trustoo binds its own trigger, which opens the form in place. Reloading
      // with ?req=review only opens it some of the time, so prefer the trigger
      // and keep the href as the fallback for a modified click or a late script.
      const isPlainClick = clickEvent
        && clickEvent.button === 0
        && !clickEvent.metaKey && !clickEvent.ctrlKey && !clickEvent.shiftKey && !clickEvent.altKey;
      const appTrigger = document.querySelector('.tt-write-reviews');
      if (appTrigger && (!clickEvent || isPlainClick)) {
        clickEvent?.preventDefault();
        appTrigger.click();
        return;
      }

      // A real link is deliberately used so review writing still works if this
      // script loads late or fails. Let the browser handle regular and modified
      // clicks instead of duplicating the navigation here.
      if (clickEvent && configuredUrl) return;

      const reviewUrl = new URL(configuredUrl || window.location.href, window.location.href);
      if (!configuredUrl) reviewUrl.searchParams.set('req', 'review');

      window.location.assign(reviewUrl.toString());
    }

    bindV2Events() {
      const elements = this.v2Elements;
      if (!elements) return;

      elements.mediaTabs.forEach((tab) => {
        tab.addEventListener('click', () => {
          if (tab.disabled) return;
          this.v2State.mediaType = tab.dataset.prMediaTab || 'all';
          this.updateV2MediaTabs();
          this.renderV2MediaStrip();
        });
        tab.addEventListener('keydown', (event) => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          const enabledTabs = elements.mediaTabs.filter((candidate) => !candidate.disabled);
          const currentIndex = enabledTabs.indexOf(tab);
          if (currentIndex < 0 || !enabledTabs.length) return;
          event.preventDefault();
          let nextIndex = currentIndex;
          if (event.key === 'Home') nextIndex = 0;
          else if (event.key === 'End') nextIndex = enabledTabs.length - 1;
          else if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % enabledTabs.length;
          else nextIndex = (currentIndex - 1 + enabledTabs.length) % enabledTabs.length;
          enabledTabs[nextIndex].focus();
          enabledTabs[nextIndex].click();
        });
      });
      elements.mediaPrevious?.addEventListener('click', () => this.scrollV2Media(-1));
      elements.mediaNext?.addEventListener('click', () => this.scrollV2Media(1));
      elements.mediaStrip?.addEventListener('scroll', () => this.updateV2MediaControls(), { passive: true });
      elements.mediaStrip?.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        this.scrollV2Media(event.key === 'ArrowRight' ? 1 : -1);
      });
      this.viewport.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        this.scrollTrack(event.key === 'ArrowRight' ? 1 : -1);
      });

      elements.search?.addEventListener('input', () => {
        this.v2State.search = elements.search.value;
        this.applyV2Filters();
      });
      elements.sort?.addEventListener('change', () => {
        this.v2State.sort = elements.sort.value;
        this.applyV2Filters({ resetVisible: false });
      });
      elements.sortButton?.addEventListener('click', (event) => {
        event.stopPropagation();
        this.setV2SortMenuOpen(!this.isV2SortMenuOpen());
      });
      elements.sortButton?.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.isV2SortMenuOpen()) {
          event.preventDefault();
          this.setV2SortMenuOpen(false);
          return;
        }
        if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        this.setV2SortMenuOpen(true, { focusOption: true });
      });
      elements.sortOptions?.forEach((option) => {
        option.addEventListener('click', (event) => {
          event.stopPropagation();
          this.selectV2Sort(option.dataset.prSortOption);
        });
        option.addEventListener('keydown', (event) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            this.setV2SortMenuOpen(false, { restoreFocus: true });
            return;
          }
          const directions = {
            ArrowDown: 1,
            ArrowUp: -1,
            Home: 'first',
            End: 'last'
          };
          if (!(event.key in directions)) return;
          event.preventDefault();
          this.moveV2SortOptionFocus(option, directions[event.key]);
        });
      });
      document.addEventListener('click', (event) => {
        if (this.isV2SortMenuOpen() && !elements.sortDropdown?.contains(event.target)) {
          this.setV2SortMenuOpen(false);
        }
      });
      elements.ratingButtons.forEach((button) => {
        button.addEventListener('click', () => {
          const rating = Number(button.dataset.prRatingFilter || 0);
          this.v2State.rating = this.v2State.rating === rating ? 0 : rating;
          this.applyV2Filters();
        });
      });
      elements.ratingSelect?.addEventListener('change', () => {
        this.v2State.rating = Number(elements.ratingSelect.value || 0);
        this.applyV2Filters();
      });
      elements.clearFilters?.addEventListener('click', () => this.clearV2Filters());
      elements.showMore?.addEventListener('click', () => {
        this.v2State.visiblePages = Math.max(1, this.v2State.visiblePages || 1) + 1;
        this.applyV2Filters({ resetVisible: false });
      });
      elements.writeReview?.addEventListener('click', (event) => this.openV2WriteReview(event));

      this.section.addEventListener('click', (event) => {
        const mediaButton = event.target.closest('[data-pr-v2-media-item]');
        if (!mediaButton) return;
        this.openReview(
          Number(mediaButton.dataset.prReviewIndex || 0),
          Number(mediaButton.dataset.prLocalPhoto || 0),
          mediaButton
        );
      });
    }

    bindEvents() {
      this.section.addEventListener('click', (event) => {
        const openButton = event.target.closest('[data-pr-open-review]');
        if (openButton) {
          const card = openButton.closest('[data-pr-card]');
          this.openReview(Number(card.dataset.prReviewIndex || 0), null, openButton);
          return;
        }

        const photoButton = event.target.closest('[data-pr-open-photo]');
        if (photoButton) {
          event.stopPropagation();
          const card = photoButton.closest('[data-pr-card]');
          const reviewIndex = Number(card.dataset.prReviewIndex || 0);
          const localIndex = Number(photoButton.dataset.prLocalPhoto || 0);
          this.openReview(reviewIndex, localIndex, photoButton);
        }
      });

      this.previousButton?.addEventListener('click', () => this.scrollTrack(-1));
      this.nextButton?.addEventListener('click', () => this.scrollTrack(1));
      this.viewport.addEventListener('scroll', () => this.updateTrackControls(), { passive: true });
      this.viewport.addEventListener('mouseenter', () => this.pauseMarquee('hover'));
      this.viewport.addEventListener('mouseleave', () => this.resumeMarquee('hover'));
      this.viewport.addEventListener('focusin', () => this.pauseMarquee('focus'));
      this.viewport.addEventListener('focusout', () => {
        window.setTimeout(() => {
          if (!this.viewport.contains(document.activeElement)) this.resumeMarquee('focus');
        }, 0);
      });
      this.viewport.addEventListener('touchstart', (event) => this.handleCarouselTouchStart(event), { passive: true });
      this.viewport.addEventListener('touchend', (event) => this.handleCarouselTouchEnd(event), { passive: true });
      window.addEventListener('resize', () => this.handleResize(), { passive: true });
      REDUCED_MOTION.addEventListener?.('change', () => this.restartMarquee());

      this.modal.addEventListener('click', (event) => {
        if (event.target.closest('[data-pr-close]') || event.target.matches('[data-pr-scrim]')) {
          this.closeModal();
          return;
        }

        const thumb = event.target.closest('[data-pr-modal-thumb]');
        if (thumb) this.showPhoto(Number(thumb.dataset.prPhotoIndex || 0));
      });

      this.photoPrevious?.addEventListener('click', () => this.showPhoto(this.activePhotoIndex - 1));
      this.photoNext?.addEventListener('click', () => this.showPhoto(this.activePhotoIndex + 1));
      this.reviewPrevious?.addEventListener('click', () => {
        if (this.isPhotoFirstV2) this.navigateV2Review(-1);
        else this.showReview(this.activeReviewIndex - 1, true);
      });
      this.reviewNext?.addEventListener('click', () => {
        if (this.isPhotoFirstV2) this.navigateV2Review(1);
        else this.showReview(this.activeReviewIndex + 1, true);
      });
      this.helpfulButton?.addEventListener('click', () => this.toggleHelpful());

      document.addEventListener('keydown', (event) => this.handleKeydown(event));
      this.dialog.addEventListener('touchstart', (event) => this.handleTouchStart(event), { passive: true });
      this.dialog.addEventListener('touchend', (event) => this.handleTouchEnd(event), { passive: true });
      if (this.isPhotoFirstV2) this.bindV2Events();
    }

    getTrackGap() {
      const styles = getComputedStyle(this.isPhotoFirstV2 ? this.primarySet : this.track);
      return Number.parseFloat(styles.columnGap || styles.gap || 0) || 0;
    }

    getCardStep() {
      const card = this.cards[0];
      if (!card) return 0;
      const width = card.getBoundingClientRect().width || Number.parseFloat(getComputedStyle(card).width) || 320;
      return width + this.getTrackGap();
    }

    startMarquee(progress = 0) {
      this.marqueeAnimation?.cancel();
      this.marqueeAnimation = null;
      const isCompact = this.updateReviewCountState();
      this.section.classList.toggle(
        'is-reduced-motion',
        !isCompact && (REDUCED_MOTION.matches || typeof this.track.animate !== 'function')
      );

      if (isCompact) {
        this.track.style.transform = '';
        this.updateTrackControls();
        return;
      }

      if (this.section.classList.contains('is-reduced-motion')) {
        this.track.style.transform = '';
        this.updateTrackControls();
        return;
      }

      const gap = this.getTrackGap();
      const measuredSetWidth = this.primarySet.getBoundingClientRect().width;
      const fallbackSetWidth = Math.max(0, (this.getCardStep() * this.cards.length) - gap);
      this.marqueeDistance = (measuredSetWidth || fallbackSetWidth) + gap;
      if (!this.marqueeDistance) return;

      this.marqueeAnimation = this.track.animate(
        [
          { transform: 'translate3d(0, 0, 0)' },
          { transform: `translate3d(-${this.marqueeDistance}px, 0, 0)` }
        ],
        {
          duration: this.marqueeDuration,
          easing: 'linear',
          iterations: Infinity
        }
      );
      this.marqueeAnimation.currentTime = Math.max(0, Math.min(progress, 1)) * this.marqueeDuration;
      if (this.pauseReasons.size) this.marqueeAnimation.pause();
      this.updateTrackControls();
    }

    restartMarquee() {
      const currentTime = Number(this.marqueeAnimation?.currentTime || 0);
      const progress = this.marqueeDuration ? (currentTime % this.marqueeDuration) / this.marqueeDuration : 0;
      this.startMarquee(progress);
    }

    handleResize() {
      window.clearTimeout(this.resizeTimer);
      this.resizeTimer = window.setTimeout(() => {
        const hasActiveV2Filters = this.isPhotoFirstV2 && this.hasV2Filters();
        if (hasActiveV2Filters) this.applyV2Filters({ resetVisible: false, animateLayout: false });
        else this.restartMarquee();
        if (this.isPhotoFirstV2) {
          this.updateV2MediaControls();
          this.updateTrackControls();
        }
        if (this.activeMediaNaturalSize) {
          this.updateModalMediaSize(
            this.activeMediaNaturalSize.width,
            this.activeMediaNaturalSize.height
          );
        }
      }, 120);
    }

    pauseMarquee(reason) {
      this.pauseReasons.add(reason);
      this.marqueeAnimation?.pause();
    }

    resumeMarquee(reason) {
      this.pauseReasons.delete(reason);
      if (!this.pauseReasons.size) this.marqueeAnimation?.play();
    }

    scrollTrack(direction) {
      const step = this.getCardStep();
      if (!step) return;

      if (this.marqueeAnimation && this.marqueeDistance) {
        const delta = (step / this.marqueeDistance) * this.marqueeDuration;
        const currentTime = Number(this.marqueeAnimation.currentTime || 0);
        this.marqueeAnimation.currentTime = (currentTime + (direction * delta) + this.marqueeDuration) % this.marqueeDuration;
        return;
      }

      this.viewport.scrollBy({
        left: direction * step,
        behavior: REDUCED_MOTION.matches ? 'auto' : 'smooth'
      });
    }

    updateTrackControls() {
      if (!this.previousButton || !this.nextButton) return;
      if (this.isPhotoFirstV2 && this.section.classList.contains('is-v2-filtered')) {
        this.previousButton.disabled = true;
        this.nextButton.disabled = true;
        return;
      }
      if (this.hasCompactReviewSet()) {
        this.previousButton.disabled = true;
        this.nextButton.disabled = true;
        return;
      }
      if (this.marqueeAnimation) {
        this.previousButton.disabled = false;
        this.nextButton.disabled = false;
        return;
      }
      const maxScroll = Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
      this.previousButton.disabled = this.viewport.scrollLeft <= 2;
      this.nextButton.disabled = this.viewport.scrollLeft >= maxScroll - 2;
    }

    handleCarouselTouchStart(event) {
      if (!event.changedTouches.length) return;
      const touch = event.changedTouches[0];
      this.carouselTouchStart = { x: touch.clientX, y: touch.clientY };
      this.pauseMarquee('touch');
    }

    handleCarouselTouchEnd(event) {
      if (!this.carouselTouchStart || !event.changedTouches.length) return;
      const touch = event.changedTouches[0];
      const deltaX = touch.clientX - this.carouselTouchStart.x;
      const deltaY = touch.clientY - this.carouselTouchStart.y;
      this.carouselTouchStart = null;
      if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 40) {
        this.scrollTrack(deltaX < 0 ? 1 : -1);
      }
      this.resumeMarquee('touch');
    }

    openReview(reviewIndex, photoIndex, trigger) {
      if (!this.reviews[reviewIndex]) return;
      window.clearTimeout(this.closeTimer);
      this.returnFocus = trigger || document.activeElement;
      this.pauseMarquee('modal');
      this.modal.hidden = false;
      document.body.classList.add('pr-modal-open');
      this.showReview(reviewIndex, false, photoIndex);
      requestAnimationFrame(() => {
        this.modal.classList.add('is-open');
        this.modal.querySelector('[data-pr-close]')?.focus({ preventScroll: true });
      });
    }

    closeModal() {
      if (this.modal.hidden) return;
      this.stopActiveMedia();
      this.modal.classList.remove('is-open');
      document.body.classList.remove('pr-modal-open');
      const finish = () => {
        this.modal.hidden = true;
        this.returnFocus?.focus?.({ preventScroll: true });
        this.resumeMarquee('modal');
      };
      if (REDUCED_MOTION.matches) finish();
      else this.closeTimer = window.setTimeout(finish, 170);
    }

    renderModalReviewDetails(review, boundedIndex) {
      this.activeReviewIndex = boundedIndex;
      this.body.textContent = review.body;
      this.variant.textContent = review.variant;
      this.variant.hidden = !review.variant;
      this.avatar.textContent = review.initial;
      this.name.textContent = review.name;
      this.date.textContent = [review.date, review.verified ? this.section.dataset.prVerifiedText : '']
        .filter(Boolean)
        .join(' · ');
      this.stars.innerHTML = review.starsMarkup;
      this.stars.setAttribute('aria-label', `${review.rating} out of 5 stars`);
      this.updateHelpful();

      if (review.variantUrl) {
        this.shopLink.hidden = false;
        this.shopLink.href = review.variantUrl;
        this.shopHint.hidden = false;
        this.shopHint.textContent = this.section.dataset.prShopHint.replace('[variant]', review.variant || 'this configuration');
      } else {
        this.shopLink.hidden = true;
        this.shopHint.hidden = true;
      }

      if (this.isPhotoFirstV2) {
        const displayedOrder = this.v2DisplayedReviewIndices.includes(boundedIndex)
          ? this.v2DisplayedReviewIndices
          : this.reviews.map((item) => item.index);
        const position = displayedOrder.indexOf(boundedIndex);
        this.reviewPrevious.disabled = position <= 0;
        this.reviewNext.disabled = position < 0 || position === displayedOrder.length - 1;
      } else {
        this.reviewPrevious.disabled = boundedIndex === 0;
        this.reviewNext.disabled = boundedIndex === this.reviews.length - 1;
      }
    }

    animateV2ReviewChange(previousIndex, nextIndex) {
      if (
        !this.isPhotoFirstV2
        || REDUCED_MOTION.matches
        || previousIndex === nextIndex
        || !this.modal.classList.contains('is-open')
        || typeof this.modalDetails?.animate !== 'function'
      ) return;

      const displayedOrder = this.v2DisplayedReviewIndices.length
        ? this.v2DisplayedReviewIndices
        : this.reviews.map((review) => review.index);
      const previousPosition = displayedOrder.indexOf(previousIndex);
      const nextPosition = displayedOrder.indexOf(nextIndex);
      const direction = previousPosition >= 0 && nextPosition >= 0
        ? (nextPosition > previousPosition ? 1 : -1)
        : (nextIndex > previousIndex ? 1 : -1);
      this.modalDetails.animate(
        [
          { opacity: 0, transform: `translate3d(${direction * 18}px, 0, 0)` },
          { opacity: 1, transform: 'translate3d(0, 0, 0)' }
        ],
        { duration: 240, easing: 'cubic-bezier(.32, .72, .24, 1)' }
      );
    }

    showReview(reviewIndex, resetPhoto = false, requestedPhotoIndex = null) {
      const boundedIndex = Math.min(Math.max(reviewIndex, 0), this.reviews.length - 1);
      const review = this.reviews[boundedIndex];
      if (!review) return;

      const previousReviewIndex = this.activeReviewIndex;
      this.renderModalReviewDetails(review, boundedIndex);
      this.animateV2ReviewChange(previousReviewIndex, boundedIndex);

      let nextPhotoIndex = requestedPhotoIndex;
      if (nextPhotoIndex === null || resetPhoto) {
        nextPhotoIndex = 0;
      }

      if (review.media.length) {
        this.modal.classList.remove('pr-modal--text-only');
        const media = this.isPhotoFirstV2 ? this.v2AllMedia : review.media;
        this.buildPhotoStrip(media);
        if (this.isPhotoFirstV2) {
          const globalIndex = media.findIndex((item) => (
            item.reviewIndex === boundedIndex && item.localIndex === Math.max(nextPhotoIndex || 0, 0)
          ));
          this.showPhoto(globalIndex >= 0 ? globalIndex : 0);
        } else {
          this.showPhoto(Math.min(Math.max(nextPhotoIndex || 0, 0), review.media.length - 1));
        }
      } else {
        this.modal.classList.add('pr-modal--text-only');
        this.activePhotoIndex = 0;
        this.activeMediaNaturalSize = null;
        this.stopActiveMedia();
        this.mainPhotoMedia.replaceChildren();
        this.thumbStrip.replaceChildren();
        this.photoCount.textContent = '';
      }
    }

    updateHelpful() {
      if (!this.helpfulButton || !this.helpfulLabel) return;
      const review = this.reviews[this.activeReviewIndex];
      const isActive = Boolean(review && this.helpfulReviews.has(String(review.id)));
      const displayedCount = review ? review.helpfulCount + (isActive ? 1 : 0) : 0;
      const activeText = this.section.dataset.prHelpfulActiveText || 'Thanks';
      this.helpfulButton.classList.toggle('is-active', isActive);
      this.helpfulButton.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      this.helpfulLabel.textContent = isActive
        ? activeText
        : this.helpfulDefaultText;

      if (this.helpfulCount) {
        this.helpfulCount.textContent = displayedCount > 0 ? String(displayedCount) : '';
        this.helpfulCount.hidden = displayedCount <= 0;
      }

      const countDescription = displayedCount === 1
        ? '1 person found this review helpful'
        : `${displayedCount} people found this review helpful`;
      this.helpfulButton.setAttribute(
        'aria-label',
        displayedCount > 0
          ? `${isActive ? activeText : this.helpfulDefaultText}. ${countDescription}`
          : this.helpfulDefaultText
      );
    }

    toggleHelpful() {
      const review = this.reviews[this.activeReviewIndex];
      if (!review) return;
      const reviewId = String(review.id);
      const isHelpful = !this.helpfulReviews.has(reviewId);
      if (isHelpful) this.helpfulReviews.add(reviewId);
      else this.helpfulReviews.delete(reviewId);
      this.persistHelpfulReviews();
      this.publishHelpfulEvent(review, isHelpful);
      this.updateHelpful();
    }

    buildPhotoStrip(media) {
      this.thumbStrip.innerHTML = '';
      media.forEach((photo, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = `pr-modal-thumb${photo.type === 2 ? ' pr-modal-thumb--video' : ''}`;
        button.dataset.prModalThumb = '';
        button.dataset.prPhotoIndex = String(index);
        button.setAttribute(
          'aria-label',
          this.isPhotoFirstV2
            ? `View customer ${photo.type === 2 ? 'video' : 'photo'} ${index + 1} of ${media.length} on this product`
            : `View customer ${photo.type === 2 ? 'video' : 'photo'} ${index + 1} of ${media.length} from this review`
        );

        const image = document.createElement('img');
        image.alt = photo.alt;
        image.width = 120;
        image.height = 120;
        image.loading = 'lazy';
        const sources = [photo.thumbnail, ...(photo.thumbnailFallbacks || []), photo.fallbackUrl];
        if (sources.some(Boolean)) {
          this.setMediaSourceWithFallback(
            image,
            sources,
            () => {
              if (photo.type === 1) button.remove();
              else image.remove();
            }
          );
          button.appendChild(image);
        }
        this.thumbStrip.appendChild(button);
      });
    }

    updateModalMediaSize(sourceWidth, sourceHeight) {
      const width = Number(sourceWidth);
      const height = Number(sourceHeight);
      if (!this.dialog || !this.mainPhoto || width <= 0 || height <= 0) return;

      this.activeMediaNaturalSize = { width, height };
      this.dialog.style.setProperty('--pr-media-ratio', `${width} / ${height}`);

      const ratio = width / height;
      this.dialog.classList.toggle('is-portrait-media', ratio < .9);
      this.dialog.classList.toggle('is-landscape-media', ratio > 1.1);
      this.dialog.classList.toggle('is-square-media', ratio >= .9 && ratio <= 1.1);

      if (window.innerWidth <= 767) return;

      const isTablet = window.innerWidth <= 899;
      const maximumWidth = isTablet
        ? Math.min(480, Math.max(220, window.innerWidth - 80))
        : Math.min(440, Math.max(240, window.innerWidth - 528));
      const maximumHeight = isTablet
        ? Math.min(360, Math.max(220, window.innerHeight * .52))
        : Math.min(420, Math.max(240, window.innerHeight - 191));

      let renderedWidth = maximumWidth;
      let renderedHeight = renderedWidth / ratio;
      if (renderedHeight > maximumHeight) {
        renderedHeight = maximumHeight;
        renderedWidth = renderedHeight * ratio;
      }

      this.dialog.style.setProperty('--pr-media-width', `${Math.round(renderedWidth)}px`);
      this.dialog.style.setProperty('--pr-media-height', `${Math.round(renderedHeight)}px`);
    }

    stopActiveMedia() {
      window.clearTimeout(this.videoLoadTimer);
      this.videoLoadTimer = null;

      const currentMedia = this.mainPhotoMedia?.firstElementChild;
      currentMedia?.__prCleanup?.();

      if (currentMedia?.tagName === 'VIDEO') {
        try {
          currentMedia.pause();
          currentMedia.removeAttribute('src');
          currentMedia.load();
        } catch (error) {
          // Removing the element still stops playback if the browser blocks cleanup.
        }
      } else if (currentMedia?.tagName === 'IFRAME') {
        currentMedia.src = 'about:blank';
      }
    }

    renderVideoPlayerFrame(photo) {
      const playerUrl = this.getCloudflareStreamPlayerUrl(photo.playerUrl)
        || this.getCloudflareStreamPlayerUrl(photo.thumbnail)
        || this.getCloudflareStreamPlayerUrl(photo.url)
        || this.normalizeMediaUrl(photo.playerUrl);
      if (!playerUrl || this.isLikelyImageUrl(playerUrl)) {
        this.renderVideoUnavailable(photo);
        return;
      }

      const frame = document.createElement('iframe');
      frame.className = 'pr-video-frame';
      frame.title = photo.alt || 'Customer review video';
      frame.src = playerUrl;
      frame.allow = 'autoplay; fullscreen; picture-in-picture';
      frame.allowFullscreen = true;
      frame.referrerPolicy = 'strict-origin-when-cross-origin';
      frame.addEventListener('load', () => {
        if (this.mainPhotoMedia.firstElementChild === frame) {
          this.dialog.classList.remove('is-media-loading');
        }
      }, { once: true });

      this.mainPhotoMedia.replaceChildren(frame);
      this.updateModalMediaSize(photo.width || 1, photo.height || 1);
      this.videoLoadTimer = window.setTimeout(() => {
        this.dialog.classList.remove('is-media-loading');
      }, 2000);
    }

    renderVideoUnavailable(photo) {
      const message = document.createElement('div');
      message.className = 'pr-video-error';
      message.setAttribute('role', 'status');

      const title = document.createElement('strong');
      title.textContent = 'Video unavailable';
      const detail = document.createElement('span');
      detail.textContent = 'This customer video could not be loaded.';
      message.append(title, detail);

      const playerUrl = this.normalizeMediaUrl(photo.playerUrl);
      if (playerUrl) {
        const link = document.createElement('a');
        link.href = playerUrl;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.textContent = 'Open video';
        message.appendChild(link);
      }

      this.mainPhotoMedia.replaceChildren(message);
      this.updateModalMediaSize(photo.width || 1, photo.height || 1);
      this.dialog.classList.remove('is-media-loading');
    }

    loadVideoWithFallback(video, photo) {
      const sources = this.getVideoSourceCandidates([
        ...(photo.videoSources || []),
        photo.url,
        photo.fallbackUrl
      ], photo.thumbnail);
      let sourceIndex = 0;
      let attempt = 0;
      let active = true;

      const resetVideo = () => {
        window.clearTimeout(this.videoLoadTimer);
        this.videoLoadTimer = null;
        attempt += 1;
        video.onerror = null;
        video.onloadedmetadata = null;
        try {
          video.pause();
          video.removeAttribute('src');
          video.load();
        } catch (error) {
          // The video element is replaced immediately after a failed cleanup.
        }
      };

      const usePlayerFallback = () => {
        if (!active) return;
        active = false;
        resetVideo();
        if (this.mainPhotoMedia.firstElementChild === video) this.renderVideoPlayerFrame(photo);
      };

      const tryNextSource = () => {
        if (!active || this.mainPhotoMedia.firstElementChild !== video) return;
        window.clearTimeout(this.videoLoadTimer);
        this.videoLoadTimer = null;
        video.onerror = null;
        video.onloadedmetadata = null;

        const source = sources[sourceIndex];
        sourceIndex += 1;
        if (!source) {
          usePlayerFallback();
          return;
        }

        const currentAttempt = ++attempt;
        video.onloadedmetadata = () => {
          if (!active || currentAttempt !== attempt || this.mainPhotoMedia.firstElementChild !== video) return;
          window.clearTimeout(this.videoLoadTimer);
          this.videoLoadTimer = null;
          video.onerror = null;
          video.onloadedmetadata = null;
          this.updateModalMediaSize(
            video.videoWidth || photo.width || 1,
            video.videoHeight || photo.height || 1
          );
          this.dialog.classList.remove('is-media-loading');
        };
        video.onerror = () => {
          if (currentAttempt === attempt) tryNextSource();
        };
        video.src = source;
        video.load();

        this.videoLoadTimer = window.setTimeout(() => {
          if (currentAttempt === attempt && video.readyState < 1) tryNextSource();
        }, VIDEO_LOAD_TIMEOUT);
      };

      video.__prCleanup = () => {
        if (!active) return;
        active = false;
        resetVideo();
      };

      tryNextSource();
    }

    showPhoto(index) {
      const media = this.isPhotoFirstV2
        ? this.v2AllMedia
        : (this.reviews[this.activeReviewIndex]?.media || []);
      if (!media.length) return;
      const boundedIndex = Math.min(Math.max(index, 0), media.length - 1);
      const photo = media[boundedIndex];
      if (this.isPhotoFirstV2 && photo.reviewIndex !== this.activeReviewIndex) {
        const photoReview = this.reviews[photo.reviewIndex];
        if (photoReview) this.renderModalReviewDetails(photoReview, photo.reviewIndex);
      }
      this.activePhotoIndex = boundedIndex;
      this.dialog.classList.add('is-media-loading');
      this.stopActiveMedia();

      let mediaElement;
      let applyImageSize = null;
      let loadVideo = null;
      if (photo.type === 2) {
        const streamPlayerUrl = this.getCloudflareStreamPlayerUrl(photo.playerUrl)
          || this.getCloudflareStreamPlayerUrl(photo.thumbnail)
          || this.getCloudflareStreamPlayerUrl(photo.url);
        if (streamPlayerUrl) {
          photo.playerUrl = streamPlayerUrl;
          loadVideo = () => this.renderVideoPlayerFrame(photo);
        } else {
          mediaElement = document.createElement('video');
          mediaElement.controls = true;
          mediaElement.playsInline = true;
          mediaElement.preload = 'metadata';
          if (photo.thumbnail) mediaElement.poster = photo.thumbnail;
          mediaElement.setAttribute('aria-label', photo.alt);
          loadVideo = () => this.loadVideoWithFallback(mediaElement, photo);
        }
      } else {
        mediaElement = document.createElement('img');
        mediaElement.alt = photo.alt;
        mediaElement.width = 760;
        mediaElement.height = 760;
        mediaElement.decoding = 'async';
        applyImageSize = () => {
          if (this.mainPhotoMedia.firstElementChild !== mediaElement) return;
          this.updateModalMediaSize(mediaElement.naturalWidth, mediaElement.naturalHeight);
          this.dialog.classList.remove('is-media-loading');
        };
        mediaElement.addEventListener('load', applyImageSize, { once: true });
        this.setMediaSourceWithFallback(
          mediaElement,
          [photo.url, photo.fallbackUrl],
          () => this.dialog.classList.remove('is-media-loading')
        );
      }
      if (mediaElement) this.mainPhotoMedia.replaceChildren(mediaElement);
      loadVideo?.();
      if (this.isPhotoFirstV2 && !REDUCED_MOTION.matches && typeof this.mainPhotoMedia.animate === 'function') {
        this.mainPhotoMedia.animate(
          [{ opacity: 0 }, { opacity: 1 }],
          { duration: 200, easing: 'ease-out' }
        );
      }
      if (applyImageSize && mediaElement.complete && mediaElement.naturalWidth) {
        applyImageSize();
      }

      const configuredCountText = this.section.dataset.prPhotoCountText || 'photos in this review';
      const countText = this.isPhotoFirstV2
        ? 'every photo on this product'
        : media.some((item) => item.type === 2)
        ? 'media in this review'
        : configuredCountText === 'every photo on this product'
        ? 'photos in this review'
        : configuredCountText;
      this.photoCount.textContent = `${boundedIndex + 1} of ${media.length} · ${countText}`;
      this.photoPrevious.disabled = boundedIndex === 0;
      this.photoNext.disabled = boundedIndex === media.length - 1;

      this.thumbStrip.querySelectorAll('[data-pr-modal-thumb]').forEach((thumb, thumbIndex) => {
        const isActive = thumbIndex === boundedIndex;
        thumb.classList.toggle('is-active', isActive);
        thumb.setAttribute('aria-current', isActive ? 'true' : 'false');
        if (isActive) thumb.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'nearest' });
      });
    }

    handleKeydown(event) {
      if (this.modal.hidden) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeModal();
        return;
      }

      if (event.key !== 'Tab') return;
      const focusable = Array.from(this.dialog.querySelectorAll('button:not(:disabled), a[href], [tabindex]:not([tabindex="-1"])'))
        .filter((element) => !element.hidden && element.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    handleTouchStart(event) {
      if (window.innerWidth > 767 || !event.changedTouches.length) return;
      const touch = event.changedTouches[0];
      this.touchStart = { x: touch.clientX, y: touch.clientY };
    }

    handleTouchEnd(event) {
      if (!this.touchStart || window.innerWidth > 767 || !event.changedTouches.length) return;
      const touch = event.changedTouches[0];
      const deltaX = touch.clientX - this.touchStart.x;
      const deltaY = touch.clientY - this.touchStart.y;
      this.touchStart = null;

      if (Math.abs(deltaY) > Math.abs(deltaX) && deltaY > 80) {
        this.closeModal();
      } else if (Math.abs(deltaX) > 60) {
        if (this.isPhotoFirstV2) {
          this.navigateV2Review(deltaX < 0 ? 1 : -1);
        } else if (deltaX < 0 && this.activeReviewIndex < this.reviews.length - 1) {
          this.showReview(this.activeReviewIndex + 1, true);
        } else if (deltaX > 0 && this.activeReviewIndex > 0) {
          this.showReview(this.activeReviewIndex - 1, true);
        }
      }
    }
  }

  const init = (root = document) => {
    root.querySelectorAll(SELECTOR).forEach((section) => {
      if (section.dataset.prInitialized === 'true') return;
      section.dataset.prInitialized = 'true';
      new ProductReviewsPanel(section);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
